import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useLanguage } from "@/lib/LanguageContext";
import { HOSPITAL, whatsappLink } from "@/lib/i18n";
import { resolveImageUrl } from "@/lib/media";
import { motion, AnimatePresence } from "framer-motion";
import * as LucideIcons from "lucide-react";
import {
  Phone,
  ArrowRight,
  Users,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fallback slides (used only when backend is unreachable or returns empty)
const FALLBACK_SLIDES = [
  {
    id: "slide-1",
    image:
      "https://images.unsplash.com/photo-1597807037496-c56a1d8bc29a?crop=entropy&cs=srgb&fm=jpg&w=1800&q=80",
    caption_id: "Fasilitas Modern & Terpercaya",
    caption_en: "Modern & Trusted Facility",
  },
];

const FALLBACK_STATS = [
  { id: "s1", icon: "Users", value: "20+", label_id: "Dokter Spesialis", label_en: "Specialist Doctors" },
  { id: "s2", icon: "HeartPulse", value: "15+", label_id: "Layanan Medis", label_en: "Medical Services" },
  { id: "s3", icon: "Clock", value: "24/7", label_id: "IGD Siaga", label_en: "Emergency On-call" },
  { id: "s4", icon: "Shield", value: "25+", label_id: "Tahun Pengalaman", label_en: "Years of Experience" },
];

// Resolve a Lucide icon component from a string name, with a safe default.
const resolveIcon = (name) => {
  if (!name) return Users;
  return LucideIcons[name] || Users;
};

const AUTOPLAY_MS = 5000;

const pickLang = (s, lang, key) => {
  const v = s?.[`${key}_${lang}`];
  if (v && String(v).trim()) return v;
  // Fallback to the other language if the requested one is empty
  const alt = lang === "id" ? "en" : "id";
  return s?.[`${key}_${alt}`] || "";
};

const Hero = () => {
  const { t, lang } = useLanguage();
  const [slides, setSlides] = useState([]);
  const [statItems, setStatItems] = useState([]);
  const [index, setIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/hero-slides`)
      .then((r) => {
        const list = Array.isArray(r.data) && r.data.length > 0 ? r.data : FALLBACK_SLIDES;
        setSlides(list);
      })
      .catch(() => setSlides(FALLBACK_SLIDES));

    axios
      .get(`${API}/hero-stats`)
      .then((r) => {
        const list = Array.isArray(r.data) && r.data.length > 0 ? r.data : FALLBACK_STATS;
        setStatItems(list);
      })
      .catch(() => setStatItems(FALLBACK_STATS));
  }, []);

  const total = slides.length;

  const next = useCallback(
    () => setIndex((i) => (total > 0 ? (i + 1) % total : 0)),
    [total]
  );
  const prev = useCallback(
    () => setIndex((i) => (total > 0 ? (i - 1 + total) % total : 0)),
    [total]
  );

  useEffect(() => {
    if (isPaused || total <= 1) return undefined;
    const tm = setInterval(next, AUTOPLAY_MS);
    return () => clearInterval(tm);
  }, [next, isPaused, total]);

  const stats = statItems.map((s, i) => ({
    icon: resolveIcon(s.icon),
    value: s.value,
    label: lang === "en" ? (s.label_en || s.label_id) : (s.label_id || s.label_en),
    testid: `stat-${s.id || i}`,
  }));

  if (total === 0) {
    return (
      <section
        id="home"
        data-testid="hero-section"
        className="relative pt-16 md:pt-20 overflow-hidden bg-slate-950 h-[92vh] min-h-[640px]"
      />
    );
  }

  const active = slides[Math.min(index, total - 1)];

  // Resolve per-slide texts with sensible i18n fallbacks
  const badge = pickLang(active, lang, "badge") || t.hero.badge;
  const title1 = pickLang(active, lang, "title_line1") || t.hero.title_line1;
  const title2 = pickLang(active, lang, "title_line2") || t.hero.title_line2;
  const subtitle = pickLang(active, lang, "subtitle") || t.hero.subtitle;
  const captionText = pickLang(active, lang, "caption") || "";
  const ctaPrimaryText = pickLang(active, lang, "cta_primary_text") || t.hero.cta_primary;
  const ctaSecondaryText = pickLang(active, lang, "cta_secondary_text") || t.hero.cta_secondary;
  const ctaSecondaryUrl = active?.cta_secondary_url || "#services";
  const ctaPrimaryUrl =
    active?.cta_primary_url ||
    whatsappLink(
      lang === "id"
        ? "Halo RS Bhayangkara Tarakan, saya ingin membuat janji temu."
        : "Hello RS Bhayangkara Tarakan, I would like to book an appointment."
    );

  return (
    <section
      id="home"
      data-testid="hero-section"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      className="relative pt-16 md:pt-20 overflow-hidden bg-slate-950"
    >
      <div
        className="relative h-[92vh] min-h-[640px] max-h-[820px] w-full"
        data-testid="hero-carousel"
      >
        <AnimatePresence mode="sync">
          <motion.div
            key={active.id || index}
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.02 }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0"
          >
            <img
              src={resolveImageUrl(active.image)}
              alt={captionText || title1}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-950/85 via-slate-950/60 to-slate-950/20" />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-transparent to-slate-950/30" />
          </motion.div>
        </AnimatePresence>

        <div className="relative z-10 h-full container-x flex items-center">
          <div className="max-w-2xl">
            <motion.div
              key={`badge-${index}`}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              data-testid="hero-badge"
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur text-white/90 text-xs font-semibold uppercase tracking-[0.15em] mb-6"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-blue-400 animate-pulse" />
              {badge}
            </motion.div>

            <motion.h1
              key={`title-${index}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              data-testid="hero-title"
              className="font-heading text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight text-white leading-[1.02]"
            >
              {title1}
              {title2 && (
                <>
                  <br />
                  <span className="text-blue-300">{title2}</span>
                </>
              )}
            </motion.h1>

            <motion.p
              key={`sub-${index}`}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.25 }}
              data-testid="hero-subtitle"
              className="mt-6 text-base md:text-lg leading-relaxed text-slate-200/90 max-w-xl"
            >
              {subtitle}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.35 }}
              className="mt-8 flex flex-col sm:flex-row gap-3"
            >
              <a
                href={ctaPrimaryUrl}
                target={ctaPrimaryUrl.startsWith("#") ? "_self" : "_blank"}
                rel="noreferrer"
                data-testid="hero-appointment-btn"
                className="group inline-flex items-center justify-center gap-2 bg-white hover:bg-blue-50 text-blue-900 font-medium px-6 py-3.5 rounded-full transition-all shadow-xl shadow-blue-900/30"
              >
                <Phone className="h-4 w-4" />
                {ctaPrimaryText}
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href={ctaSecondaryUrl}
                data-testid="hero-services-btn"
                className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 hover:border-white text-white font-medium px-6 py-3.5 rounded-full transition-all backdrop-blur"
              >
                {ctaSecondaryText}
              </a>
            </motion.div>

            {captionText && (
              <motion.div
                key={`cap-${index}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                data-testid="hero-caption"
                className="hidden md:flex items-center gap-3 mt-10 text-white/70 text-sm"
              >
                <div className="h-px w-10 bg-white/40" />
                <span>{captionText}</span>
              </motion.div>
            )}
          </div>
        </div>

        {total > 1 && (
          <div
            className="absolute bottom-6 md:bottom-8 left-0 right-0 z-20 flex items-center justify-center gap-2"
            data-testid="hero-dots"
          >
            {slides.map((s, i) => (
              <button
                key={s.id || i}
                onClick={() => setIndex(i)}
                data-testid={`hero-dot-${i}`}
                aria-label={`Slide ${i + 1}`}
                className={`h-1.5 rounded-full transition-all duration-500 ${
                  i === index ? "w-10 bg-white" : "w-2 bg-white/40 hover:bg-white/70"
                }`}
              />
            ))}
          </div>
        )}

        {total > 1 && (
          <>
            <button
              onClick={prev}
              data-testid="hero-prev"
              aria-label="Previous slide"
              className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-20 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 border border-white/30 backdrop-blur text-white items-center justify-center transition"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={next}
              data-testid="hero-next"
              aria-label="Next slide"
              className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-20 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 border border-white/30 backdrop-blur text-white items-center justify-center transition"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </>
        )}

        <div className="absolute top-6 right-4 md:top-8 md:right-8 z-20 hidden sm:block">
          <div className="bg-white/95 backdrop-blur rounded-2xl shadow-2xl shadow-blue-900/20 p-3 flex items-center gap-3">
            <img
              src={HOSPITAL.logo}
              alt="RS Bhayangkara Tarakan"
              className="h-12 w-12 rounded-xl object-cover"
            />
            <div className="pr-2">
              <div className="font-heading font-bold text-slate-900 text-sm leading-tight">
                RS Bhayangkara
              </div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider">
                {HOSPITAL.affiliation}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="relative -mt-10 md:-mt-14 z-10 container-x">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.7 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-0 bg-white rounded-3xl shadow-2xl shadow-blue-900/10 border border-slate-100 overflow-hidden"
        >
          {stats.map((s, i) => (
            <div
              key={s.label}
              data-testid={s.testid}
              className={`p-5 md:p-7 flex items-center gap-4 ${
                i < stats.length - 1 ? "md:border-r border-slate-100" : ""
              } ${i % 2 === 0 ? "border-r md:border-r border-slate-100" : ""} ${
                i < 2 ? "border-b md:border-b-0 border-slate-100" : ""
              }`}
            >
              <div className="h-11 w-11 md:h-12 md:w-12 rounded-xl bg-blue-100 text-blue-800 flex items-center justify-center shrink-0">
                <s.icon className="h-5 w-5 md:h-6 md:w-6" />
              </div>
              <div>
                <div className="font-heading text-2xl md:text-3xl font-bold text-slate-900 tabular-nums leading-none">
                  {s.value}
                </div>
                <div className="text-xs md:text-sm text-slate-500 mt-1">{s.label}</div>
              </div>
            </div>
          ))}
        </motion.div>
      </div>

      <div className="h-16 md:h-20 bg-white" />
    </section>
  );
};

export default Hero;
